import { Button, DialogV2, Typography } from '@recruit-robin/robin-theme/web-components';
import { Star_06 } from '@recruit-robin/robin-theme/web-icons';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';

import { toOutreachEditorTokens } from '@/entities/candidate';
import type { GenerateAiReachoutMessageEditorRef } from '@/features/generate-ai-reachout-message';
import { useDictionary } from '@/shared/lib/i18n';

import { BulkProgressBar } from '../bulk-progress-bar';
import { BulkCandidateRow } from './BulkCandidateRow';
import { useStyles } from './BulkGenerationProgress.styles';
import type { BulkGenerationProgressProps, BulkGenerationProgressRef } from './BulkGenerationProgress.types';
import type { BulkCandidate } from '../rj-comm-bulk-widget/RjCommBulkWidget.types';

function hasSubject(data: unknown): data is { subject: string; message: string } {
  return data != null && typeof data === 'object' && 'subject' in data;
}

export const BulkGenerationProgress = forwardRef<BulkGenerationProgressRef, BulkGenerationProgressProps>(
  function BulkGenerationProgress(
    { isVisible, jobImprovedId, slots, isComplete, candidates, onComplete, onBack, onDelete, onRegenerate, onClose },
    ref,
  ) {
    const classes = useStyles();
    const dictionary = useDictionary();
    const editorRefsMap = useRef(new Map<string, GenerateAiReachoutMessageEditorRef>());

    const tokensMap = useMemo(() => {
      const map = new Map<string, Record<string, string>>();
      for (const candidate of candidates) {
        map.set(candidate.esPersonId, toOutreachEditorTokens(candidate));
      }
      return map;
    }, [candidates]);

    const commAgentFlowByEsPersonId = useMemo(() => {
      const map = new Map<string, BulkCandidate['commAgentFlow']>();
      for (const candidate of candidates) {
        map.set(candidate.esPersonId, candidate.commAgentFlow);
      }
      return map;
    }, [candidates]);

    const handleRegisterEditorRef = useCallback(
      (esPersonId: string, editorRef: GenerateAiReachoutMessageEditorRef | null) => {
        if (editorRef) {
          editorRefsMap.current.set(esPersonId, editorRef);
        } else {
          editorRefsMap.current.delete(esPersonId);
        }
      },
      [],
    );

    const handleDelete = useCallback(
      (esPersonId: string) => {
        editorRefsMap.current.delete(esPersonId);
        onDelete(esPersonId);
      },
      [onDelete],
    );

    useImperativeHandle(
      ref,
      () => ({
        getResults: () =>
          Array.from(slots.values())
            .filter((slot) => slot.status === 'success' && slot.data != null)
            .map((slot) => {
              const editorRef = editorRefsMap.current.get(slot.esPersonId);
              if (editorRef) {
                const edited = editorRef.getContent();
                return { esPersonId: slot.esPersonId, subject: edited.subject, message: edited.message };
              }
              return {
                esPersonId: slot.esPersonId,
                subject: hasSubject(slot.data) ? slot.data!.subject : '',
                message: slot.data!.message,
              };
            }),
      }),
      [slots],
    );

    if (!isVisible) return null;

    const slotList = Array.from(slots.values());
    const completed = slotList.filter((s) => s.status === 'success').length;
    const failed = slotList.filter((s) => s.status === 'error').length;

    const headerDescription =
      isComplete && failed === 0
        ? dictionary.bulkAllMessagesReadyReviewBeforeSend
        : failed > 0
          ? dictionary.bulkSomeMessagesCouldNotBeGeneratedReviewBeforeSend
          : dictionary.robinJrBulkPersonalisingReviewBeforeSend;

    return (
      <DialogV2.Root isVisible={true} customCss={classes.dialog}>
        <DialogV2.Backdrop />
        <DialogV2.Content customCss={classes.content}>
          <DialogV2.Close onRequestClose={onClose} />
          <DialogV2.Header customCss={classes.header}>
            <Star_06 size={24} color={(c) => c.primary.fuchsia[40]} />
            <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
              {dictionary.reviewOutreachMessage}
            </Typography>
            <div css={classes.headerDescription}>
              <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[60]}>
                {headerDescription}
              </Typography>
            </div>
            <BulkProgressBar
              completed={completed}
              failed={failed}
              total={slotList.length}
              isComplete={isComplete}
              labels={{
                inProgress: dictionary.bulkGenerating,
                allDone: dictionary.bulkAllMessagesReady,
                nFailed: dictionary.bulkNFailed,
                doneWithFailures: dictionary.bulkGenerationDoneWithFailures,
              }}
            />
          </DialogV2.Header>

          <DialogV2.Body customCss={classes.body}>
            <div css={classes.list}>
              {slotList.map((slot, index) => (
                <div key={slot.esPersonId} css={index < slotList.length - 1 && classes.rowSeparator}>
                  <BulkCandidateRow
                    key={slot.esPersonId}
                    slot={slot}
                    jobImprovedId={jobImprovedId}
                    tokens={tokensMap.get(slot.esPersonId) ?? {}}
                    showSubject={commAgentFlowByEsPersonId.get(slot.esPersonId) !== 'whatsapp'}
                    isWhatsapp={commAgentFlowByEsPersonId.get(slot.esPersonId) === 'whatsapp'}
                    isIndeedFlow={commAgentFlowByEsPersonId.get(slot.esPersonId) === 'indeed'}
                    onRegisterEditorRef={handleRegisterEditorRef}
                    onDelete={handleDelete}
                    onRegenerate={onRegenerate}
                  />
                </div>
              ))}
            </div>
          </DialogV2.Body>

          <DialogV2.Footer customCss={classes.footer}>
            <div css={classes.footerActions}>
              <Button label={dictionary.back} onClick={onBack} style="text" type="subtle2" />
              <Button
                label={dictionary.confirmAndStart}
                onClick={onComplete}
                style="filled"
                type="success"
                disabled={!isComplete}
              />
            </div>
          </DialogV2.Footer>
        </DialogV2.Content>
      </DialogV2.Root>
    );
  },
);
